const base = '/homepage/assets';

export type CaseStudy = {
  id: string;
  tag: string;
  venue: string;
  title: string;
  body: string;
  metrics: { num: string; lbl: string }[];
  image: string;
  pos?: string;
  fit?: 'cover' | 'contain';
};

export const CASE_STUDIES: CaseStudy[] = [
  {
    tag: 'Interpretive Signage',
    venue: '',
    id: 'interpretive-signage',
    title: 'How Jacksonville Zoo and Zoo New England Are Turning Interpretive Signs Into Multilingual Guest Experiences',
    body: 'See how Jacksonville Zoo and Zoo New England are using CheQRboard to turn traditional interpretive signage into multilingual, interactive guest experiences. From offering educational content in a dozen languages to adding trivia, fun facts, audio tours, conservation messaging, and thoughtful donation prompts, both venues are helping guests engage more deeply with animals and learn in the language they are most comfortable with.',
    metrics: [
      { num: '133+', lbl: 'languages on every sign' },
      { num: '2.8x', lbl: 'more time at exhibits' },
    ],
    image: `${base}/cs-eagle.jpg`,
  },
  {
    tag: 'Exhibit Accessibility',
    venue: '',
    id: 'exhibit-accessibility',
    title: 'How Zoo New England and the NC Museum of Natural Sciences Made Exhibits More Accessible With CheQRboard',
    body: 'See how ZNE and NCMNS make exhibit content more accessible, inclusive, and engaging for guests. From ZNE’s new multilingual penguin exhibit to NCMNS’s Blue Whales special exhibition, both venues used QR codes to give visitors instant access to educational content in their preferred language without replacing or overcrowding existing signage.',
    metrics: [
      { num: '12.5K', lbl: 'scans in a single month' },
      { num: '3.4x', lbl: 'more engagement than before' },
    ],
    image: `${base}/cs-donations-opt.jpg`,
  },
  {
    tag: 'Multilingual Surveys',
    venue: '',
    id: 'multilingual-surveys',
    title: 'How Multilingual Surveys Help Venues Capture Better Guest Feedback',
    body: 'Collecting visitor feedback is one of the best ways to understand what is working, what needs improvement, and how guests are experiencing an exhibit, program, or venue. But traditional surveys often miss a major part of the audience: guests who would rather respond in a language other than English. CheQRboard surveys are available in 133+ languages, making it easier for venues to collect feedback from a broader and more representative group of visitors.',
    metrics: [
      { num: '6.3K', lbl: 'multilingual survey responses' },
      { num: '133+', lbl: 'languages available' },
    ],
    image: `${base}/cs-survey-wall-opt.jpg`,
  },
  {
    tag: 'Sponsorships',
    venue: '',
    id: 'sponsorships',
    title: 'How Venues Are Turning Sponsorships Into Interactive Guest Experiences',
    body: 'Traditional sponsorships often rely on logo placement, banners, or signs near an exhibit. While those placements can create visibility, they are often passive and difficult to measure. CheQRboard gives venues a new way to make sponsorship more interactive, memorable, and measurable by connecting brands to games, scavenger hunts, conservation trivia, audio tours, donation matching campaigns, and story-driven guest experiences.',
    metrics: [
      { num: '22K', lbl: 'scans in 30 days' },
      { num: '$250K+', lbl: 'in sponsor funding' },
    ],
    image: `${base}/cs-sponsorships-card.jpg`,
  },
  {
    tag: 'Offsite Engagement',
    venue: '',
    id: 'offsite-engagement',
    title: 'Extending the Guest Experience Beyond the Venue',
    body: 'Most visitor engagement ends when a guest leaves the venue. But some of the most creative CheQRboard use cases happen outside the venue entirely, in classrooms, homes, hospitals, restaurants, breweries, and other community spaces. By placing dynamic QR codes on everyday objects like posters, fridge magnets, coasters, flyers, and signs, venues are creating new ways to stay connected with members, students, families, donors, and future visitors long after the original visit is over.',
    metrics: [
      { num: '8.3K', lbl: 'offsite scans' },
      { num: '15', lbl: 'offsite locations' },
    ],
    image: `${base}/cs-beyond-opt.jpg`,
  },
  {
    tag: 'Citizen Science',
    venue: 'Zoo New England',
    id: 'citizen-science',
    title: 'How Zoos Are Turning Guests Into Wildlife Researchers',
    body: 'Understanding animal behavior is an important part of animal care, conservation, and exhibit management. Traditionally, collecting this kind of behavioral data can require significant staff time, with team members or volunteers observing animals, recording behaviors, and compiling data over long periods of time. Zoo New England is using CheQRboard to make that process more interactive by inviting visitors to participate in behavioral observation through QR-powered signage.',
    metrics: [
      { num: '2.9K', lbl: 'guest observations' },
      { num: '18', lbl: 'habitats covered' },
    ],
    image: `${base}/cs-penguin-behaviors-opt.jpg`,
  },
  {
    tag: 'Frictionless Giving',
    venue: 'Army Aviation Museum',
    id: 'frictionless-giving',
    title: 'How Venues Are Turning Guest Engagement Into Frictionless Donations',
    body: 'See how venues are turning guest engagement into frictionless giving. By pairing creative donation asks with educational content, trivia, fun facts, behind-the-scenes stories, and one-click payment options like Apple Pay, venues can inspire guests to give at the moment they feel most connected. From “feed an animal for the day” campaigns to mission-driven museum donations, CheQRboard helps partners make giving easier, more emotional, and more measurable.',
    metrics: [
      { num: '$11K', lbl: 'raised in a month' },
      { num: '14s', lbl: 'average time to donate' },
    ],
    image: `${base}/cs-coyote-opt.jpg`,
    pos: 'center 82%',
  },
  {
    tag: 'Line-Time Engagement',
    venue: 'Regional Amusement Park',
    id: 'line-time-engagement',
    title: 'Turning Queue Time Into Guest Engagement',
    body: 'Guests waiting in the aquarium queue scanned QR codes along the route to follow an interactive sea turtle rescue journey, transforming wait time into an engaging educational experience while raising awareness about sea turtle conservation and the threats these endangered animals face.',
    metrics: [
      { num: '38%', lbl: 'lower perceived wait time' },
      { num: '12K+', lbl: 'games played / month' },
    ],
    image: `${base}/cs-line-time-opt.jpg`,
  },
];

export const FEATURED_CASE_STUDY =
  CASE_STUDIES.find((s) => s.id === 'interpretive-signage') || CASE_STUDIES[0];

export const MORE_CASE_STUDIES = CASE_STUDIES.filter((s) => s !== FEATURED_CASE_STUDY);
